/**
 * Group Thread Tags — local styled-components for the flag-gated tag UI.
 *
 * Kept in a dedicated module (NOT the 115KB shared `styles.ts`) so the tag UI is
 * a contained, additive footprint. Visual language matches the chat chips
 * (Inter, pill radius, gold base) with restrained semantic tones per status /
 * priority so a thread's workflow state reads at a glance.
 */
import styled from 'styled-components';

export type ChipTone =
  | 'neutral'
  | 'info'
  | 'warn'
  | 'danger'
  | 'success'
  | 'muted';

const TONE: Record<ChipTone, { bg: string; fg: string; border: string }> = {
  neutral: { bg: 'rgba(198, 167, 94, 0.14)', fg: '#8a6d2f', border: 'rgba(182, 146, 76, 0.45)' },
  // Brand-consistent (2026-08-12): informational chips ride the warm ink
  // family — only true STATE tones (warn/danger/success) keep semantic hues.
  info: { bg: 'rgba(107, 100, 89, 0.10)', fg: '#57503F', border: 'rgba(107, 100, 89, 0.38)' },
  warn: { bg: 'rgba(217, 149, 43, 0.14)', fg: '#a15c07', border: 'rgba(217, 149, 43, 0.42)' },
  danger: { bg: 'rgba(220, 38, 38, 0.12)', fg: '#b91c1c', border: 'rgba(220, 38, 38, 0.40)' },
  success: { bg: 'rgba(22, 163, 74, 0.12)', fg: '#15803d', border: 'rgba(22, 163, 74, 0.40)' },
  muted: { bg: 'rgba(100, 116, 139, 0.12)', fg: '#475569', border: 'rgba(100, 116, 139, 0.35)' },
};

export const TagChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

export const TagChip = styled.span<{ $tone?: ChipTone }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 180px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 999px;
  color: ${(p) => TONE[p.$tone ?? 'neutral'].fg};
  background: ${(p) => TONE[p.$tone ?? 'neutral'].bg};
  border: 1px solid ${(p) => TONE[p.$tone ?? 'neutral'].border};
`;

export const TagChipButton = styled.button<{ $tone?: ChipTone }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 180px;
  padding: 3px 10px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.01em;
  white-space: nowrap;
  border-radius: 999px;
  cursor: pointer;
  color: ${(p) => TONE[p.$tone ?? 'neutral'].fg};
  background: ${(p) => TONE[p.$tone ?? 'neutral'].bg};
  border: 1px solid ${(p) => TONE[p.$tone ?? 'neutral'].border};
  transition: filter 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    filter: brightness(0.97);
  }
  &:focus-visible {
    outline: 2px solid ${(p) => TONE[p.$tone ?? 'neutral'].border};
    outline-offset: 1px;
  }
`;

/** Tiny × affordance inside a removable label chip. */
export const TagChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  margin-left: 1px;
  padding: 0;
  font-size: 12px;
  line-height: 1;
  color: inherit;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.65;

  &:hover {
    opacity: 1;
  }
`;

export const TagFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
`;

export const TagFilterSelect = styled.select`
  font-family: inherit;
  font-size: 12px;
  padding: 3px 6px;
  color: #475569;
  background: var(--brand-canvas);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 8px;
  cursor: pointer;
`;

export const TagFilterLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #8a6d2f;
`;
